// Format axios / fetch errors into specific, localized, user-friendly
// strings for the Customer Portal. Replaces the generic "Something went
// wrong" toast/banner with messages that actually describe the problem.
//
// Order of precedence:
//  1. Known HTTP status (or network error) → mapped to a LOCALIZED key.
//     This always wins over backend detail to keep the language consistent
//     with the user's selected locale (the backend currently returns
//     English-only detail strings).
//  2. Unknown status with a readable backend `detail` → use detail.
//  3. Final generic fallback.
//
// `context` lets the same helper produce slightly different wording for
// the lookup form vs. save vs. reschedule vs. cancel flows.

export function formatPortalError(err, t, context = "generic") {
  if (!err || typeof err !== "object") {
    return t("portal.errors.generic");
  }

  // Network / no response (server unreachable, DNS, CORS, offline).
  if (!err.response) {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return t("portal.errors.offline");
    }
    if (err.code === "ECONNABORTED") {
      return t("portal.errors.timeout");
    }
    return t("portal.errors.network");
  }

  const { status, data } = err.response;

  // 1. Localized mapping for known status codes.
  const mapped = mapStatusToKey(status, context);
  if (mapped) return t(mapped);

  // 2. Trust a server-provided readable detail for anything we don't recognise.
  const detail = typeof data === "object" && data && typeof data.detail === "string"
    ? data.detail.trim()
    : "";
  if (detail && /\s/.test(detail) && detail.length > 4) {
    return detail;
  }

  // 3. Generic fallback.
  return t("portal.errors.generic");
}

function mapStatusToKey(status, context) {
  switch (status) {
    case 400:
      return "portal.errors.bad_request";
    case 401:
      return "portal.errors.session_expired";
    case 403:
      return context === "lookup"
        ? "portal.errors.lookup_mismatch"
        : "portal.errors.forbidden";
    case 404:
      return context === "lookup"
        ? "portal.errors.lookup_not_found"
        : "portal.errors.not_found";
    case 409:
      if (context === "reschedule") return "portal.errors.slot_taken";
      if (context === "cancel") return "portal.errors.already_cancelled";
      return "portal.errors.conflict";
    case 410:
      return "portal.errors.gone";
    case 422:
      return "portal.errors.validation";
    case 429:
      return "portal.errors.rate_limited";
    default:
      if (status >= 500 && status < 600) return "portal.errors.server";
      return null;
  }
}
